import React from "react";
import { Share2, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { inferRidePaceClass, PACE_STAT_LABEL } from "../lib/ride";

const BASE = process.env.REACT_APP_BACKEND_URL;

// Share links point at the server-rendered /api/r/:id page so WhatsApp,
// Messages and Slack unfurl with the ride name, stats and route map.
export const rideShareUrl = (ride) => `${BASE}/api/r/${ride.id}`;

export function rideShareText(ride) {
  const when = [ride.day, ride.date, ride.time].filter(Boolean).join(" · ");
  const pace = PACE_STAT_LABEL[inferRidePaceClass(ride)];
  const stats = [ride.distance, ride.elevation && `${ride.elevation} climbing`, pace && `${pace} pace`]
    .filter(Boolean)
    .join(" · ");
  const lines = [`🚴 GLCC · ${ride.name || "Club ride"}${when ? ` — ${when}` : ""}`];
  if (stats) lines.push(stats);
  const detail = [ride.location && `Depart ${ride.location}`, ride.cafe && `Coffee after at ${ride.cafe}`]
    .filter(Boolean)
    .join(". ");
  if (detail) lines.push(`${detail}.`);
  return lines.join("\n");
}

export function weekShareText(rides) {
  const lines = ["🚴 GLCC · This week's rides"];
  rides.forEach((r) => {
    const when = [r.day, r.date, r.time].filter(Boolean).join(" ");
    const stats = [r.distance, r.elevation].filter(Boolean).join(" · ");
    lines.push(`• ${r.name}${when ? ` — ${when}` : ""}${stats ? ` (${stats})` : ""}`);
  });
  return lines.join("\n");
}

async function handOff({ title, text, url }) {
  const payload = { title, text, url };
  if (navigator.share) {
    try {
      await navigator.share(payload);
      return;
    } catch (e) {
      if (e?.name === "AbortError") return; // user closed the sheet
    }
  }
  const joined = [text, url].filter(Boolean).join("\n");
  try {
    await navigator.clipboard.writeText(joined);
    toast.success("Copied — paste it anywhere");
  } catch (e) {
    toast.error("Couldn't share on this device");
  }
}

export default function ShareRide({ ride, weekRides = [] }) {
  const shareThis = () =>
    handOff({ title: `GLCC · ${ride.name}`, text: rideShareText(ride), url: rideShareUrl(ride) });

  const upcoming = weekRides.filter((r) => r.id !== ride.id);
  const shareWeek = () => {
    const all = [ride, ...upcoming];
    handOff({ title: "GLCC · This week's rides", text: weekShareText(all), url: BASE });
  };

  return (
    <div className="mt-2 flex items-center gap-2" data-testid="share-ride">
      <button
        type="button"
        onClick={shareThis}
        className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-accent-volt text-black uppercase tracking-widest text-[11px] font-black py-2.5 active:scale-[0.98] transition-transform"
        data-testid="share-ride-button"
      >
        <Share2 className="w-4 h-4" /> Share this ride
      </button>
      {upcoming.length > 0 && (
        <button
          type="button"
          onClick={shareWeek}
          className="flex-none flex items-center gap-1.5 rounded-xl border border-border-subtle bg-bg-secondary text-text-secondary uppercase tracking-widest text-[11px] font-bold px-3 py-2.5 active:scale-[0.98] transition-transform"
          data-testid="share-week-button"
        >
          <CalendarDays className="w-4 h-4" /> Week
        </button>
      )}
    </div>
  );
}
